import React from "react";
import { DrawerForm, DrawerFormRef } from "../drawers/DrawerForm";
import Icon from "../base/Icon";
import { setPlanNotice, toShortDateWithoutTime, useHub } from "../../core/Global";
import { toastError, toastSuccess } from "../base/Toast";
import "../../modules/system/settings/components/PlanChange.css";

// Taking back a scheduled plan change -- a downgrade, an upgrade booked for the renewal date,
// or a pause -- with a look at what that means before it is done. Opened from the header's
// notice pill and from the subscription page alike, so both ask the same way.
export default function CancelChangeDrawer(props: {
    /** "downgrade", "upgrade" or "hold". */
    kind?: string,
    /** The plan the account is on now. */
    currentPlanName?: string,
    /** The plan the change would have moved it to (not for a pause). */
    pendingPlanName?: string,
    /** When the change would have happened: the renewal date. */
    effectiveAt?: Date | string,
    drawerRef?: React.RefObject<DrawerFormRef>,
    onClose: () => void,
    onCancelled: () => void
}) {

    const hub = useHub()
    const hold = props.kind === "hold"
    const upgrade = props.kind === "upgrade"
    const when = toShortDateWithoutTime(props.effectiveAt ? new Date(props.effectiveAt) : undefined)
    const current = props.currentPlanName ?? "your plan"

    const title = hold ? "Cancel the pause" : upgrade ? "Cancel the upgrade" : "Cancel the downgrade"
    const scheduled = hold
        ? `Your account is set to be paused on ${when}.`
        : `Your plan is set to change to ${props.pendingPlanName ?? "another plan"} on ${when}.`

    const cancel = (callback: any) => {
        hub.Post("/accounts/profile/plan/change/cancel", {}, res => {
            callback(res)
            if (res.success) {
                toastSuccess(res.data?.message ?? "The change has been cancelled.")
                // The header's pill is this change's banner; it goes with it.
                setPlanNotice(undefined)
                props.onCancelled()
            }
            else toastError(res.message ?? "Couldn't cancel the change.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-cancel-plan-change"
            ref={props.drawerRef}
            title={title}
            titleIcon="rotate-left"
            subtitle={scheduled}
            size="medium"
            buttonText="Cancel the change"
            buttonColor="danger"
            onClose={props.onClose}
            onSubmit={(body, callback) => cancel(callback)}>

            <div className="plan-change">
                <div className="plan-change-summary mb-8">
                    <div className="plan-change-row">
                        <span>Scheduled</span>
                        <b>{hold ? "Pause" : `${upgrade ? "Upgrade" : "Downgrade"} to ${props.pendingPlanName ?? ""}`}</b>
                    </div>
                    <div className="plan-change-row">
                        <span>Would have happened on</span>
                        <b>{when} — your renewal date</b>
                    </div>
                    <div className="plan-change-row">
                        <span>If you cancel it</span>
                        <b>You stay on {current}</b>
                    </div>
                </div>

                <div className="plan-change-note is-success mb-8">
                    <Icon icon="circle-check" type="solid" size={14} />
                    <div>
                        {
                            hold
                                ? <>Your account will not be paused. {current} carries on and renews as usual on <b>{when}</b>.</>
                                : <>Nothing changes on {when}: {current} carries on and renews as usual, at the price you pay today.</>
                        }
                    </div>
                </div>

                <div className="fs-8 text-gray-500">
                    You can schedule the change again any time from Change Plan.
                </div>
            </div>
        </DrawerForm>
    )
}
